import React, { useEffect, useReducer, useState } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./App.css"; // CSS dosyamızı ekliyoruz

// ---------------------------
// Reducer & initial state
// ---------------------------
const PAGE_SIZE_DEFAULT = 6;

const initialState = {
  loading: false,
  error: null,
  query: "friends",
  filters: { genre: "All", language: "All", minRating: 0 },
  results: [],
  page: 1,
  pageSize: PAGE_SIZE_DEFAULT,
  watchlist: [],
};

function reducer(state, action) {
  switch (action.type) {
    case "FETCH_INIT": return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS": return { ...state, loading: false, results: action.payload, page: 1 };
    case "FETCH_FAILURE": return { ...state, loading: false, error: action.payload };
    case "SET_QUERY": return { ...state, query: action.payload };
    case "SET_FILTERS": return { ...state, filters: { ...state.filters, ...action.payload }, page: 1 };
    case "SET_WATCHLIST": return { ...state, watchlist: action.payload };
    case "SET_PAGE_SIZE": return { ...state, pageSize: action.payload, page: 1 };
    case "ADD_WATCHLIST":
      if (state.watchlist.find((s) => s.id === action.payload.id)) return state;
      return { ...state, watchlist: [...state.watchlist, action.payload] };
    case "REMOVE_WATCHLIST": return { ...state, watchlist: state.watchlist.filter((s) => s.id !== action.payload) };
    case "CLEAR_WATCHLIST": return { ...state, watchlist: [] };
    case "SET_PAGE": return { ...state, page: action.payload };
    default: return state;
  }
}

// ---------------------------
// Utility
// ---------------------------
function applyFilters(shows, filters) {
  return shows.filter((show) => {
    const rating = show.rating?.average || 0;
    const matchesRating = rating >= (filters.minRating || 0);
    const matchesLanguage = filters.language === "All" || show.language === filters.language;
    const genres = show.genres || [];
    const matchesGenre = filters.genre === "All" || genres.includes(filters.genre);
    return matchesRating && matchesLanguage && matchesGenre;
  });
}

// ---------------------------
// Components
// ---------------------------
function SearchBox({ query, onQueryChange, onSearch }) {
  const [local, setLocal] = useState(query);

  useEffect(() => setLocal(query), [query]);
  function submit(e) { e.preventDefault(); onQueryChange(local); onSearch(local); }

  return (
    <form onSubmit={submit} className="search-box">
      <input
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder="Ara (örn: friends)"
      />
      <button type="submit">Ara</button>
    </form>
  );
}

function Filters({ filters, onChange, availableGenres, availableLanguages }) {
  return (
    <div className="filters">
      <label>
        Tür:
        <select value={filters.genre} onChange={(e) => onChange({ genre: e.target.value })}>
          <option value="All">Tümü</option>
          {availableGenres.map((g) => (<option key={g} value={g}>{g}</option>))}
        </select>
      </label>
      <label>
        Dil:
        <select value={filters.language} onChange={(e) => onChange({ language: e.target.value })}>
          <option value="All">Tümü</option>
          {availableLanguages.map((l) => (<option key={l} value={l}>{l}</option>))}
        </select>
      </label>
      <label>
        Min Puan:
        <input
          type="number"
          min="0"
          max="10"
          step="0.1"
          value={filters.minRating}
          onChange={(e) => onChange({ minRating: Number(e.target.value) })}
        />
      </label>
    </div>
  );
}

function TVCard({ show, onAdd, onViewDetail }) {
  const summary = show.summary ? show.summary.replace(/<[^>]+>/g, "") : "Özet yok";
  const image = show.image?.medium;
  const rating = show.rating?.average ?? "—";
  const genres = (show.genres || []).join(", ") || "—";

  return (
    <div className="tv-card">
      {image ? <img src={image} alt={show.name} /> : <div className="no-image">No Image</div>}
      <div className="tv-card-body">
        <h3>{show.name}</h3>
        <p>Tür: {genres} • Dil: {show.language} • Puan: {rating}</p>
        <p>{summary.slice(0, 120)}{summary.length > 120 ? "..." : ""}</p>
        <div className="tv-card-buttons">
          <button onClick={() => onViewDetail(show.id)}>Detay</button>
          <button onClick={() => onAdd(show)}>Kısa Listeye Ekle</button>
        </div>
      </div>
    </div>
  );
}

function TVList({ shows, loading, error, onAdd, onViewDetail }) {
  if (loading) return <div className="status">Yükleniyor...</div>;
  if (error) return <div className="status">Hata: {String(error)}</div>;
  if (!shows.length) return <div className="status">Sonuç bulunamadı.</div>;
  return (
    <div className="tv-list">
      {shows.map((show) => <TVCard key={show.id} show={show} onAdd={onAdd} onViewDetail={onViewDetail} />)}
    </div>
  );
}

function WatchlistPanel({ watchlist, onRemove, onClear }) {
  return (
    <div className="watchlist-panel">
      <h4>Gösterime Girecekler ({watchlist.length})</h4>
      {watchlist.length === 0 && <div>Henüz eklenmiş dizi yok.</div>}
      <ul>
        {watchlist.map((s) => (
          <li key={s.id}>
            <Link to={`/show/${s.id}`}>{s.name}</Link>
            <button onClick={() => onRemove(s.id)}>Kaldır</button>
          </li>
        ))}
      </ul>
      {watchlist.length > 0 && <button onClick={onClear}>Temizle</button>}
    </div>
  );
}

function Pagination({ page, totalPages, onSetPage }) {
  if (totalPages === 0) return null;
  return (
    <div className="pagination">
      <button onClick={() => onSetPage(1)} disabled={page===1}>İlk</button>
      <button onClick={() => onSetPage(page-1)} disabled={page===1}>Geri</button>
      <span>Sayfa {page} / {totalPages}</span>
      <button onClick={() => onSetPage(page+1)} disabled={page===totalPages}>İleri</button>
      <button onClick={() => onSetPage(totalPages)} disabled={page===totalPages}>Son</button>
    </div>
  );
}

function Footer({ name }) {
  return (
    <footer className="footer">
      &copy; {new Date().getFullYear()} {name} - Süleyman Demirel Üniversitesi Film Kulübü
    </footer>
  );
}

// ---------------------------
// Pages
// ---------------------------
function Home({ state, dispatch }) {
  const { loading, error, results, query, filters, page, pageSize, watchlist } = state;
  const navigate = useNavigate();
  const allShows = results || [];
  const availableGenres = Array.from(new Set(allShows.flatMap((s) => s.genres || []))).sort();
  const availableLanguages = Array.from(new Set(allShows.map((s) => s.language).filter(Boolean))).sort();
  const filtered = applyFilters(allShows, filters);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage-1)*pageSize;
  const pagedShows = filtered.slice(start, start+pageSize);

  function handleAdd(show) { dispatch({ type: "ADD_WATCHLIST", payload: show }); }

  return (
    <div className="container">
      <div className="main-content">
        <div className="left-panel">
          <SearchBox query={query} onQueryChange={(q)=>dispatch({type:"SET_QUERY",payload:q})} onSearch={(q)=>dispatchFetch(dispatch,q)} />
          <Filters filters={filters} onChange={(f)=>dispatch({type:"SET_FILTERS",payload:f})} availableGenres={availableGenres} availableLanguages={availableLanguages} />
          <TVList shows={pagedShows} loading={loading} error={error} onAdd={handleAdd} onViewDetail={(id)=>navigate(`/show/${id}`)} />
          <Pagination page={currentPage} totalPages={totalPages} onSetPage={(p)=>dispatch({type:"SET_PAGE",payload:Math.max(1,Math.min(totalPages,p))})} />
        </div>
        <aside className="right-panel">
          <WatchlistPanel watchlist={watchlist} onRemove={(id)=>dispatch({type:"REMOVE_WATCHLIST",payload:id})} onClear={()=>dispatch({type:"CLEAR_WATCHLIST"})} />
        </aside>
      </div>
      <Footer name="Arzu Küçük" />
    </div>
  );
}

function ShowDetailPage() {
  const { id } = useParams();
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState(null);
  const [show,setShow] = useState(null);
  const [episodes,setEpisodes] = useState([]);

  useEffect(()=>{
    let cancelled=false;
    async function load(){
      setLoading(true);
      try {
        const [showRes, epsRes] = await Promise.all([
          axios.get(`https://api.tvmaze.com/shows/${id}`),
          axios.get(`https://api.tvmaze.com/shows/${id}/episodes`),
        ]);
        if(cancelled) return;
        setShow(showRes.data);
        setEpisodes(epsRes.data||[]);
        setError(null);
      } catch(err){ if(cancelled) return; setError(err.message||"Hata"); } 
      finally{ if(!cancelled) setLoading(false); }
    }
    load();
    return ()=>{ cancelled=true; }
  },[id]);

  if(loading) return <div className="status">Yükleniyor...</div>;
  if(error) return <div className="status">Hata: {String(error)}</div>;
  if(!show) return <div className="status">Dizi bulunamadı.</div>;

  return (
    <div className="container">
      <div className="detail-page">
        <div className="detail-image">{show.image ? <img src={show.image.medium} alt={show.name}/> : <div className="no-image">No Image</div>}</div>
        <div className="detail-info">
          <h1>{show.name}</h1>
          <p>Dil: {show.language}</p>
          <p>Tür: {(show.genres||[]).join(", ")||"—"}</p>
          <p>Puan: {show.rating?.average||"—"}</p>
          <div>
            <h2>Bölümler</h2>
            <ul className="episode-list">
              {episodes.map((ep)=>(<li key={ep.id}>{ep.season}x{ep.number} - {ep.name}</li>))}
            </ul>
          </div>
        </div>
      </div>
      <div className="back-button"><Link to="/">Geri</Link></div>
    </div>
  );
}

// ---------------------------
// Fetch function
// ---------------------------
async function dispatchFetch(dispatch, query) {
  dispatch({ type:"FETCH_INIT" });
  try{
    const res = await axios.get(`https://api.tvmaze.com/search/shows?q=${query}`);
    const shows = res.data.map((item)=>item.show);
    dispatch({type:"FETCH_SUCCESS", payload:shows});
  }catch(err){
    dispatch({type:"FETCH_FAILURE", payload:err.message||"Hata"});
  }
}

// ---------------------------
// Main App
// ---------------------------
export default function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(()=>{ dispatchFetch(dispatch,state.query); },[]);
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home state={state} dispatch={dispatch}/>}/>
        <Route path="/show/:id" element={<ShowDetailPage/>}/>
      </Routes>
    </Router>
  );
}
